import * as React from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faCaretDown,
  faCheck,
  faCircleQuestion,
  faIdCard,
  faUser,
} from '@fortawesome/pro-solid-svg-icons'
import {
  faBank,
  faEllipsisVertical,
  faSeedling,
  faStore,
} from '@fortawesome/pro-regular-svg-icons'
import { mtc, TextColors, TypographyStyles } from '../../../utils/style-helpers'
import { Button } from '../../atoms/button'
import { Card, CardColors } from '../../atoms/card'
import { Dropdown } from '../../atoms/dropdown'
import { Icon } from '../../atoms/icon'
import { Image } from '../../atoms/image'
import { Text } from '../../atoms/text'
import { Textarea } from '../../atoms/textarea'
import { categoryFieldStyles } from './styles'

/** Transaction review card type - drives category icon/color and default copy */
export enum TransactionCardType {
  BUSINESS = 'business',
  PERSONAL = 'personal',
  UNKNOWN = 'unknown',
  FLAG = 'flag',
  BULK = 'bulk',
}

interface TransactionCardVariantConfig {
  /** Fallback category icon, used when no categoryEmoji is given (ignored for FLAG - see categoryFieldBg doc) */
  categoryIcon: IconDefinition
  categoryIconColor: string
  /** Category field background - only Unknown differs from the default white */
  categoryFieldBg: string
  categoryPlaceholder: string
  confirmLabel: string
}

/**
 * Single source of truth for everything that varies by type. Collapses what
 * were 4 parallel Record<TransactionCardType, …> maps (plus a 5th type-keyed
 * variant list in styles.ts) into one - each new variant is one entry here
 * instead of an edit in 5 places.
 */
const VARIANT_CONFIG: Record<
  TransactionCardType,
  TransactionCardVariantConfig
> = {
  [TransactionCardType.BUSINESS]: {
    categoryIcon: faIdCard,
    categoryIconColor: 'text-green-200',
    categoryFieldBg: 'bg-neutral-0',
    categoryPlaceholder: 'Select type or category...',
    confirmLabel: 'Confirm',
  },
  [TransactionCardType.PERSONAL]: {
    categoryIcon: faUser,
    categoryIconColor: 'text-blue-200',
    categoryFieldBg: 'bg-neutral-0',
    categoryPlaceholder: 'Select type or category...',
    confirmLabel: 'Confirm',
  },
  [TransactionCardType.UNKNOWN]: {
    categoryIcon: faCircleQuestion,
    categoryIconColor: 'text-yellow-100',
    categoryFieldBg: 'bg-yellow-0',
    categoryPlaceholder: 'Select type or category...',
    confirmLabel: 'Save',
  },
  [TransactionCardType.FLAG]: {
    // categoryIcon/categoryIconColor are unused - Flag always shows its own
    // fixed 🚩 emoji (see CategoryField) rather than an icon from this config.
    categoryIcon: faIdCard,
    categoryIconColor: 'text-green-200',
    categoryFieldBg: 'bg-neutral-0',
    categoryPlaceholder: 'Flag for bookkeeping team',
    confirmLabel: 'Send Message',
  },
  [TransactionCardType.BULK]: {
    categoryIcon: faIdCard,
    categoryIconColor: 'text-green-200',
    categoryFieldBg: 'bg-neutral-0',
    categoryPlaceholder: 'Select type or category...',
    confirmLabel: 'Confirm All',
  },
}

/**
 * Renders the bordered icon + label + caret field used for a category
 * selection - shared between the main card and each row of a Bulk card.
 * Hand-rolled rather than the Select atom: Select is a full Radix select
 * requiring real option data, but the category taxonomy is intentionally
 * out of scope for this component (see categoryEmoji doc below) - this is
 * a static display trigger only.
 */
function CategoryField({
  type,
  category,
  categoryEmoji,
  onClick,
  className,
}: {
  type: TransactionCardType
  category?: string
  categoryEmoji?: string
  onClick?: () => void
  className?: string
}) {
  const config = VARIANT_CONFIG[type]
  const showCustomEmoji =
    Boolean(categoryEmoji) &&
    type !== TransactionCardType.UNKNOWN &&
    type !== TransactionCardType.FLAG

  const content = (
    <>
      {type === TransactionCardType.FLAG ? (
        // Figma uses this emoji directly (not a FontAwesome icon) as the
        // fixed, non-taxonomy chrome for the Flag state.
        <span className="text-base leading-none" aria-hidden="true">
          🚩
        </span>
      ) : showCustomEmoji ? (
        // Decorative icon substitute, not textual copy - aria-hidden since
        // the category label text below already conveys this.
        <span className="text-base leading-none" aria-hidden="true">
          {categoryEmoji}
        </span>
      ) : (
        <Icon icon={config.categoryIcon} color={config.categoryIconColor} />
      )}
      <Text
        variant={TypographyStyles.BODY_SM}
        color={category ? TextColors.DEFAULT : TextColors.DISABLED}
        className="flex-1 truncate"
      >
        {category ?? config.categoryPlaceholder}
      </Text>
      <Icon icon={faCaretDown} color="text-neutral-500" />
    </>
  )
  const fieldClassName = mtc(
    categoryFieldStyles(),
    config.categoryFieldBg,
    className
  )

  // A plain div (not a disabled button) when there's no onClick: this field
  // is never going to become interactive later, so "disabled" semantics -
  // which pull it out of the focus order and a11y tree - would misrepresent
  // it as a temporarily-inoperable control rather than static content.
  if (!onClick) {
    return <div className={fieldClassName}>{content}</div>
  }

  return (
    <button type="button" onClick={onClick} className={fieldClassName}>
      {content}
    </button>
  )
}

export interface TransactionMenuItem {
  /** Menu item label, e.g. "View Details" */
  label: string
  /** Icon shown before the label, e.g. faEye for "View Details" */
  icon?: IconDefinition
  /** Called when the item is clicked */
  onClick: () => void
  /** Renders a separator above this item - e.g. to set a destructive action apart */
  withSeparatorBefore?: boolean
  /** Styles the label (and icon) as a destructive action, e.g. "Delete Transaction" */
  danger?: boolean
}

export interface TransactionBulkItem {
  /** Stable key for this row - falls back to array index if omitted. Rows commonly share the same date/amount (see Figma), so index alone isn't a reliable key. */
  id?: string
  /** Pre-formatted date, e.g. "Mar 1, 2026" */
  date: string
  /** Pre-formatted amount, e.g. "$500.00" */
  amount: string
  /** Selected category/type label for this row. Omit to show the placeholder */
  category?: string
  /** Emoji shown in place of this row's category icon - see categoryEmoji doc below */
  categoryEmoji?: string
  /** Called when this row's category field is clicked; renders as static text if omitted */
  onCategoryClick?: () => void
}

interface TransactionBaseProps {
  /** Bank/account label, e.g. "Bank of America Credit Card ••••1111" */
  bankName: string
  /** Small round bank/institution icon shown next to bankName */
  bankIconSrc?: string
  /**
   * Overflow ("...") menu items, e.g. View Details / Upload Receipt / Flag
   * for Bookkeeping Team / Mark as Duplicate. This component just renders
   * a Dropdown from this list; it doesn't know about transaction actions
   * itself, so callers decide which actions apply. Omit to hide the menu
   * icon entirely.
   */
  menuItems?: TransactionMenuItem[]
  /** Merchant name, e.g. "Psychology Today" */
  merchantName: string
  /** Merchant logo shown at 40x40 */
  merchantLogoSrc?: string
  /**
   * Pre-formatted subtitle under the merchant name - a date (e.g. "Mar 2,
   * 2025") for most types, or a transaction count (e.g. "3 transactions")
   * for the Bulk type. This component does not format dates itself.
   */
  date: string
  /** Pre-formatted amount, e.g. "$49.99" (the bulk total, for the Bulk type) */
  amount: string
  className?: string
}

/**
 * Every prop that only applies to *some* variants, all in one place so the
 * per-variant types below (via OnlyAllow) can't drift out of sync with each
 * other. Not exported - TransactionProps is the public shape.
 */
interface TransactionVariantProps {
  /** Selected category/type label, e.g. "Membership fees". Omit to show the placeholder */
  category?: string
  /**
   * Emoji to show in place of the category icon (e.g. from the category-group
   * taxonomy). Ignored for the Unknown and Flag types, which always show
   * their own fixed icon - this component does not resolve categories to
   * their emoji itself, it just renders whatever is passed in.
   */
  categoryEmoji?: string
  /** Called when the category field is clicked; field renders as static text if omitted */
  onCategoryClick?: () => void
  onAskHavenClick?: () => void
  onConfirmClick?: () => void
  /** Disables the primary action button (e.g. no category selected yet) */
  confirmDisabled?: boolean
  /** Rows shown below the merchant header for the Bulk type, each with its own category field */
  bulkItems?: TransactionBulkItem[]
  /** Note text for the Flag type's textarea */
  note?: string
  /** Called with the new value as the Flag type's textarea changes */
  onNoteChange?: (value: string) => void
  /** Placeholder for the Flag type's textarea */
  notePlaceholder?: string
  /** Called when the Flag type's "Send Message" button is clicked */
  onSendMessage?: () => void
  /**
   * Disables the Flag type's "Send Message" button. The button is always
   * disabled while note is empty, regardless of this prop - use this to
   * additionally disable it once there's a note (e.g. while sending).
   */
  sendMessageDisabled?: boolean
}

/**
 * Picks the variant props relevant to one type and pins every other variant
 * prop to `undefined` - so e.g. passing `note` while `type="bulk"` is a type
 * error instead of a silent no-op, while every branch still structurally
 * has every key (as `undefined` where irrelevant) so the component body can
 * destructure all of them at once without a per-branch narrowing dance.
 */
type OnlyAllow<K extends keyof TransactionVariantProps> = Pick<
  TransactionVariantProps,
  K
> & { [P in Exclude<keyof TransactionVariantProps, K>]?: undefined }

type TransactionStandardProps = {
  type?:
    | TransactionCardType.BUSINESS
    | TransactionCardType.PERSONAL
    | TransactionCardType.UNKNOWN
} & OnlyAllow<
  | 'category'
  | 'categoryEmoji'
  | 'onCategoryClick'
  | 'onAskHavenClick'
  | 'onConfirmClick'
  | 'confirmDisabled'
>

type TransactionFlagProps = { type: TransactionCardType.FLAG } & OnlyAllow<
  | 'category'
  | 'onCategoryClick'
  | 'note'
  | 'onNoteChange'
  | 'notePlaceholder'
  | 'onSendMessage'
  | 'sendMessageDisabled'
>

type TransactionBulkProps = { type: TransactionCardType.BULK } & OnlyAllow<
  'onAskHavenClick' | 'onConfirmClick' | 'confirmDisabled' | 'bulkItems'
>

export type TransactionProps = TransactionBaseProps &
  (TransactionStandardProps | TransactionFlagProps | TransactionBulkProps)

export const Transaction = React.forwardRef<HTMLDivElement, TransactionProps>(
  (
    {
      type = TransactionCardType.BUSINESS,
      bankName,
      bankIconSrc,
      menuItems,
      merchantName,
      merchantLogoSrc,
      date,
      amount,
      category,
      categoryEmoji,
      onCategoryClick,
      bulkItems,
      note,
      onNoteChange,
      notePlaceholder = 'Add a note for your bookkeeping team',
      onSendMessage,
      sendMessageDisabled = false,
      onAskHavenClick,
      onConfirmClick,
      confirmDisabled = false,
      className,
    },
    ref
  ) => {
    const isFlag = type === TransactionCardType.FLAG
    const isBulk = type === TransactionCardType.BULK
    const hasNote = Boolean(note?.trim())

    return (
      <Card
        ref={ref}
        color={CardColors.LEVEL_0}
        className={mtc('shadow-md', className)}
      >
        <div className="flex flex-col gap-8 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              {bankIconSrc ? (
                <Image
                  src={bankIconSrc}
                  alt=""
                  width={24}
                  height={24}
                  className="rounded-full shrink-0"
                />
              ) : (
                <Icon
                  icon={faBank}
                  color="text-neutral-600"
                  backgroundStyle="circle"
                  size="2xs"
                  backgroundClassName="bg-neutral-100 shrink-0"
                />
              )}
              <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
                {bankName}
              </Text>
            </div>
            {menuItems && menuItems.length > 0 && (
              <Dropdown>
                <Dropdown.Trigger asChild>
                  <Button
                    variant="discreet"
                    size="square"
                    className="size-4 p-0"
                    iconRight={faEllipsisVertical}
                    aria-label="Transaction options"
                  />
                </Dropdown.Trigger>
                <Dropdown.Content align="end">
                  {menuItems.map((item) => (
                    <React.Fragment key={item.label}>
                      {item.withSeparatorBefore && <Dropdown.Separator />}
                      <Dropdown.Item onClick={item.onClick}>
                        {item.icon && (
                          <Icon
                            icon={item.icon}
                            color={
                              item.danger ? 'text-danger' : 'text-neutral-600'
                            }
                          />
                        )}
                        <Text
                          variant={TypographyStyles.BODY_SM}
                          color={item.danger ? TextColors.DANGER : undefined}
                        >
                          {item.label}
                        </Text>
                      </Dropdown.Item>
                    </React.Fragment>
                  ))}
                </Dropdown.Content>
              </Dropdown>
            )}
          </div>

          <div className="flex flex-col gap-3 w-full">
            <div className="flex items-start justify-between w-full gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {merchantLogoSrc ? (
                  <Image
                    src={merchantLogoSrc}
                    alt=""
                    width={40}
                    height={40}
                    className="rounded-lg shrink-0"
                    objectFit="cover"
                  />
                ) : (
                  <Icon
                    icon={faStore}
                    color="text-neutral-600"
                    backgroundStyle="rounded"
                    size="xs"
                    backgroundClassName="size-10 bg-neutral-100 shrink-0"
                  />
                )}
                <div className="flex flex-col gap-1 min-w-0">
                  <Text
                    variant={TypographyStyles.LABEL_MD}
                    color={TextColors.HEADER}
                    className="truncate"
                  >
                    {merchantName}
                  </Text>
                  <Text
                    variant={TypographyStyles.BODY_XS}
                    color={TextColors.MUTED}
                  >
                    {date}
                  </Text>
                </div>
              </div>
              <Text
                variant={TypographyStyles.LABEL_MD}
                color={TextColors.HEADER}
              >
                {amount}
              </Text>
            </div>

            {isBulk ? (
              bulkItems &&
              bulkItems.length > 0 && (
                <div className="flex flex-col gap-3 w-full">
                  {bulkItems.map((item, index) => (
                    <div
                      key={item.id ?? index}
                      className="flex items-center gap-8 w-full"
                    >
                      <div className="flex flex-col w-24 shrink-0">
                        <Text
                          variant={TypographyStyles.BODY_XS}
                          color={TextColors.MUTED}
                        >
                          {item.date}
                        </Text>
                        <Text
                          variant={TypographyStyles.BODY_SM}
                          color={TextColors.HEADER}
                        >
                          {item.amount}
                        </Text>
                      </div>
                      {/*
                        Hardcoded rather than per-item: the Figma spec
                        doesn't vary this icon by row, only by categoryEmoji
                        - BUSINESS just supplies a sane fallback glyph/color
                        when no categoryEmoji is given.
                      */}
                      <CategoryField
                        type={TransactionCardType.BUSINESS}
                        category={item.category}
                        categoryEmoji={item.categoryEmoji}
                        onClick={item.onCategoryClick}
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              )
            ) : (
              <CategoryField
                type={type}
                category={category}
                categoryEmoji={categoryEmoji}
                onClick={onCategoryClick}
              />
            )}

            {isFlag && (
              <Textarea
                value={note ?? ''}
                onChange={(e) => onNoteChange?.(e.target.value)}
                placeholder={notePlaceholder}
                className="h-20"
              />
            )}
          </div>

          {isFlag ? (
            <Button
              variant="primary"
              size="sm"
              className="w-full"
              onClick={onSendMessage}
              disabled={!hasNote || sendMessageDisabled}
            >
              {VARIANT_CONFIG[type].confirmLabel}
            </Button>
          ) : (
            <div className="flex gap-4 w-full">
              <Button
                variant="secondary"
                size="sm"
                className="flex-1"
                iconLeft={faSeedling}
                onClick={onAskHavenClick}
              >
                Ask Haven
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="flex-1"
                iconLeft={faCheck}
                onClick={onConfirmClick}
                disabled={confirmDisabled}
              >
                {VARIANT_CONFIG[type].confirmLabel}
              </Button>
            </div>
          )}
        </div>
      </Card>
    )
  }
)

Transaction.displayName = 'Transaction'
